import { Button, cn, Label } from "@tellescope/ui";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { TellescopeFormFieldBaseProps } from "../../types";
import { AddMedicationForm } from "./AddMedicationForm";
import { useTranslation } from "react-i18next";
import { MedicationResponse } from "@tellescope/types-models";
import { FieldDescription } from "../../FieldDescription";

const EMPTY_MEDICATION: MedicationResponse = {
  displayTerm: "",
  drugName: "",
};

export interface MedicationsFieldProps extends TellescopeFormFieldBaseProps<"Medications"> {
  enduserId?: string;
}

export const MedicationsField = ({
  title,
  description,
  htmlDescription,
  value,
  onChange,
  className,
  error,
  required,
  options,
  enduserId,
  autoFocus,
}: MedicationsFieldProps) => {
  const { t } = useTranslation("forms");

  const historicalSource = options?.historicalDataSources?.find((source) => source.type === "Medications");

  const { data: historicalMedications } = useTellescopeInfiniteQuery({
    model: "enduser_medications",
    method: "getSome",
    request: { filter: { enduserId, ...historicalSource?.filter }, limit: historicalSource?.limit },
    enabled: !!historicalSource && !!enduserId,
    select: (data) =>
      data.pages.flatMap<MedicationResponse>((page) =>
        page.map((medication) => ({
          displayTerm: medication.title,
          drugName: medication.title,
          rxNormCode: medication.rxNormCode,
          fdbCode: medication.fdbCode,
          NDCs: medication.ndc ? [medication.ndc] : undefined,
          dosage: medication.dosage,
          reasonForTaking: medication.reasonForTaking,
        }))
      ),
  });

  const medications = value?.length
    ? value
    : historicalMedications?.length
    ? historicalMedications
    : [EMPTY_MEDICATION];

  const normalizedMedications = medications.reduce<MedicationResponse[]>((acc, medication) => {
    if (!medication.displayTerm && acc.some((existing) => !existing.displayTerm)) return acc;
    return [...acc, medication];
  }, []);

  const hasEmptyMedication = normalizedMedications.some((medication) => !medication.displayTerm);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {!!title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <>
        {normalizedMedications?.map((medication, i) => (
          <AddMedicationForm
            // Position is the identity here — onChange addresses entries by index, and keying by
            // medication content remounts the form on every keystroke, dropping focus.
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            medication={medication}
            dataSource={options?.dataSource}
            autoFocus={autoFocus && i === 0}
            onChange={(newMed) =>
              onChange(normalizedMedications.map((medication, j) => (i === j ? newMed : medication)))
            }
            onRemove={
              normalizedMedications.length === 1
                ? undefined
                : () => {
                    onChange([...normalizedMedications.slice(0, i), ...normalizedMedications.slice(i + 1)]);
                  }
            }
          />
        ))}
        <Button
          variant="secondary"
          disabled={hasEmptyMedication}
          onClick={() => {
            onChange([...normalizedMedications, EMPTY_MEDICATION]);
          }}
        >
          {t("Add Medication")}
        </Button>
      </>
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
